import { AfterViewInit, Component, ElementRef, Inject, Optional, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, fromEvent, merge, switchMap, tap } from 'rxjs';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import { modifiedInterval } from 'src/app/utilities/ZoneUtils';
import { ConfirmationModalComponent } from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { SpaceRecordingService } from 'src/app/services/space-recording.service';
import { PencilModalSizeEnum } from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { User } from '../../../models/user';
import { Feature } from '../../../services/acl.service';
import { FeatureManagementService } from '../../../services/feature-management.service';
import { FLAGS, FlagsService } from '../../../services/flags.service';

@Component({
  selector: 'app-wb-recorder',
  templateUrl: './wb-recorder.component.html',
  styleUrls: ['./wb-recorder.component.scss'],
})
export class WbRecorderComponent implements AfterViewInit {
  public recordingFile?: File;
  public feature = Feature;

  // Indicates that a local copy should be downloaded in addition to uploading to the cloud
  saveLocalBackup = false;
  isLoadingMetadata = true;
  pencilModalSizeEnum = PencilModalSizeEnum;
  IconTypes = IconTypes;
  @ViewChild('previewRecording') private previewVideoEl?: ElementRef<HTMLVideoElement>;

  constructor(
    protected featureManagementService: FeatureManagementService,
    @Inject(MAT_DIALOG_DATA)
    public data: { file: File; user: User; title: string; reason?: string; recordingId: string },
    @Optional() public dialogRef: MatDialogRef<WbRecorderComponent>,
    wbDialogService: WbDialogService,
    private translateService: TranslateService,
    private matDialog: MatDialog,
    private spaceRecordingService: SpaceRecordingService,
    private flagsService: FlagsService,
  ) {
    wbDialogService.pushNewDialogRef(dialogRef);
  }

  ngAfterViewInit(): void {
    if (!this.previewVideoEl) {
      return;
    }

    const objectURL = URL.createObjectURL(this.data.file);
    const vid = this.previewVideoEl.nativeElement;
    vid.src = objectURL;

    vid.preload = 'metadata';
    vid.load();

    // Poll every 3 seconds for the duration as
    // `timeupdate` does not always fire
    const durationPoll$ = modifiedInterval(3000).pipe(
      filter(() => !isNaN(vid.duration) && isFinite(vid.duration)),
      first(),
    );

    // Using the native video element doesn't show the recorded video duration before playing it -> SPAC-3414
    // Below a hacky solution to show the duration without playing the video
    const timeUpdate$ = fromEvent(vid, 'loadedmetadata').pipe(
      tap(() => (vid.currentTime = 1e101)),
      switchMap(() => fromEvent(vid, 'timeupdate')),
      first(),
    );

    // Merge the two Observables
    merge(durationPoll$, timeUpdate$)
      .pipe(first())
      .subscribe(() => {
        // Once getting the current time update, we need to reset it again to 0 to let the user plays the video normally
        vid.currentTime = 0;
        vid.controls = true;
        this.isLoadingMetadata = false;
      });
  }

  confirm(): void {
    const shouldDeleteFromIndexedDB = this.flagsService.isFlagEnabled(
      FLAGS.DELETE_LOCAL_RECORDING_AFTER_DOWNLOAD_ON_NEXT_OPEN,
    );
    this.spaceRecordingService.saveRecordingLocally(
      this.data.file,
      this.data.recordingId,
      shouldDeleteFromIndexedDB,
    );
    this.dialogRef.close();
  }

  dismiss(): void {
    this.showAreYouSureCancelSaving();
  }

  showAreYouSureCancelSaving() {
    const confirmationModalParams = {
      data: {
        title: this.translateService.instant('Discard recording?'),
        description: this.translateService.instant(
          'Are you sure you want to discard your current recording?',
        ),
        confirmButtonConfig: {
          icon: 'delete_outline',
          iconType: 'mat-icon',
          label: this.translateService.instant('Discard'),
          danger: true,
        },
        cancelButtonConfig: {
          icon: 'close',
          iconType: 'mat-icon',
          label: this.translateService.instant('Cancel'),
        },
      },
      disableClose: true,
    };

    const modalOptions = {
      afterClosed: (keepTheRecord: boolean) => {
        if (!keepTheRecord) {
          this.dialogRef.close();
        }
      },
    };

    // Used MatDialog service directly as our current ModalService is supporting only showing one-by-one modal
    // Also we have currently this case for showing nested modals
    this.matDialog
      .open(ConfirmationModalComponent, confirmationModalParams)
      .afterClosed()
      .pipe(first())
      .subscribe((val) => {
        modalOptions.afterClosed(val !== 'confirm');
      });
  }
}
